import React, { useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useDispatch } from "react-redux";
import CircularProgress from "@material-ui/core/CircularProgress";
import IconButton from "@material-ui/core/IconButton";
import { Checkbox, MenuItem, Popover, Tooltip } from "@material-ui/core";
import DmsService from "Services/DMS/dms-api.service";
import useTranslation from "Hooks/useTranslation";
import MyIcons from "assets/img/Icons/MyIcons";
import AlertToastr from "utils/alert";
import SaveCompanyModal from "Pages/PredictiveMaintenance/Companies/Modals/SaveCompanyModal";
import { setEditingCompany } from "Store/modules/mms/actions";

const CompanySlidePanelContent = ({ companyId }) => {
  const translation = useTranslation();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const inputCompanyLogoRef = useRef(null);
  const [anchorCompanyMenuEl, setAnchorCompanyMenuEl] = useState(null);
  const [isOpenSaveCompanyModal, setIsOpenSaveCompanyModal] = useState(false);

  const companyRequest = useQuery(
    ["company-with-image", companyId],
    async () => {
      const response = await DmsService.getCompanyImage(companyId);
      return response.data;
    },
    {
      enabled: !!companyId,
      refetchOnWindowFocus: false,
    },
  );

  const patchSettingsMutation = useMutation(
    (settingsPatch) => DmsService.patchCompanySettings(companyId, settingsPatch),
    {
      onMutate: async (settingsPatch) => {
        await queryClient.cancelQueries(["company-with-image", companyId]);
        const previousCompany = queryClient.getQueryData(["company-with-image", companyId]);
        queryClient.setQueryData(["company-with-image", companyId], (old) => ({
          ...(old || {}),
          settings: {
            ...(old?.settings || {}),
            ...settingsPatch,
          },
        }));
        return { previousCompany };
      },
      onError: (error, _variables, context) => {
        if (context?.previousCompany !== undefined) {
          queryClient.setQueryData(["company-with-image", companyId], context.previousCompany);
        }
        const message = error?.response?.data?.message || error?.message || "Failed to save settings";
        AlertToastr.showErrorAlert(message);
      },
      onSettled: () => {
        queryClient.invalidateQueries(["company-with-image", companyId]);
      },
    },
  );

  const handleSettingChange = (settingKey) => (event) => {
    const currentCompany = queryClient.getQueryData(["company-with-image", companyId]);
    patchSettingsMutation.mutate({
      ...(currentCompany?.settings || {}),
      [settingKey]: event.target.checked,
    });
  };

  const handleCompanyMenuOpen = (event) => {
    event.stopPropagation();
    setAnchorCompanyMenuEl(event.currentTarget);
  };

  const handleCompanyMenuClose = () => setAnchorCompanyMenuEl(null);

  const refetchCompany = () => {
    queryClient.invalidateQueries(["company-with-image", companyId]);
  };

  const onFileSelected = (file) => {
    DmsService.uploadCompanyImage(companyId, file)
      .then(async (response) => {
        if (response.data.isSuccessful) {
          refetchCompany();
        }
      })
      .catch((error) => {
        if (error?.response?.data?.message) {
          AlertToastr.showErrorAlert(error.response.data.message);
        }
      });
  };

  if (companyRequest.isLoading) {
    return (
      <div className="companySlidePanelLoader">
        <CircularProgress size={28} />
      </div>
    );
  }

  if (companyRequest.isError) {
    return (
      <div className="companySlidePanelError">
        Failed to load company details.
      </div>
    );
  }

  const company = companyRequest.data;

  if (!company) {
    return (
      <div className="companySlidePanelError">
        Company not found.
      </div>
    );
  }

  const companyName = company.name || translation.siderMyCompanyLabel;
  const displayCompanyName = companyName?.length > 25 ? `${companyName.slice(0, 25)}...` : (companyName ?? "");
  const timezoneText = company.timezone != null && company.timezone !== ""
    ? `${translation.timezoneLabel}: ${company.timezone}`
    : "";
  const displayTimezone = timezoneText.length > 30 ? `${timezoneText.slice(0, 30)}...` : timezoneText;
  const companyLogoSrc = company.imageUrl
    || (company.image ? `data:image/jpeg;base64,${company.image}` : null);
  const autoLubeReportEnabled = !!company.settings?.autoLubeReport;
  const jobPlannerEnabled = !!company.settings?.jobPlanner;
  const fleetViewEnabled = !!company.settings?.fleetView;
  const mapEnabled = !!company.settings?.map;
  const apiKeysEnabled = !!(company.settings?.apiKeys ?? company.settings?.ApiKeys);
  const wellboreDataEnabled = !!company.settings?.wellboreData;

  return (
    <>
      <input
        type="file"
        accept="image/*"
        ref={inputCompanyLogoRef}
        style={{ display: "none" }}
        onChange={(e) => {
          if (!e.target.files?.[0]) return;
          onFileSelected(e.target.files[0]);
          e.target.value = "";
        }}
      />
      <div className="companySlidePanelDetails">
        <div className="asideNavItemCompany">
          <span className="asideNavCompanyLogo">
            {companyLogoSrc ? (
              <img src={companyLogoSrc} alt="" />
            ) : (
              <MyIcons
                icon="companyIcon"
                style={{ width: 96, height: 48, fill: "rgb(0 0 0 / 16%)" }}
              />
            )}
          </span>
          <div className="asideNavCompanyNameHolder">
            <div className="asideNavCompanyNameHolderContent">
              <Tooltip title={companyName} placement="top" enterDelay={300} leaveDelay={0}>
                <span className="asideNavCompanyName">{displayCompanyName}</span>
              </Tooltip>
              {timezoneText && (
                <Tooltip title={timezoneText} placement="top" enterDelay={300} leaveDelay={0}>
                  <span className="asideNavCompanyTimezone">{displayTimezone}</span>
                </Tooltip>
              )}
            </div>
            <IconButton
              size="small"
              className="asideNavCompanyArrow"
              onClick={handleCompanyMenuOpen}
              aria-label="Menu"
            >
              <MyIcons icon="settingsIcon" />
            </IconButton>
          </div>
        </div>

        {company.description && (
          <div className="companySlidePanelSection companySlidePanelAboutSection">
            <h3 className="companySlidePanelSectionTitle">Description</h3>
            <p className="companySlidePanelDescription">{company.description}</p>
          </div>
        )}

        <div className="companySlidePanelSection companySlidePanelSettingsSection">
          <h3 className="companySlidePanelSectionTitle">{translation.headerSettings}</h3>
          <div className="companySlidePanelSettingsList">
            <label className="companySlidePanelSettingsRow">
              <span className="companySlidePanelSettingsItemLabel">Auto Lube Report</span>
              <Checkbox
                color="default"
                checked={autoLubeReportEnabled}
                disabled={patchSettingsMutation.isLoading}
                onChange={handleSettingChange("autoLubeReport")}
                className="companySlidePanelSettingsCheckbox"
              />
            </label>
            <label className="companySlidePanelSettingsRow">
              <span className="companySlidePanelSettingsItemLabel">{translation.settingsJobPlannerLabel}</span>
              <Checkbox
                color="default"
                checked={jobPlannerEnabled}
                disabled={patchSettingsMutation.isLoading}
                onChange={handleSettingChange("jobPlanner")}
                className="companySlidePanelSettingsCheckbox"
              />
            </label>
            <label className="companySlidePanelSettingsRow">
              <span className="companySlidePanelSettingsItemLabel">{translation.settingsFleetViewLabel}</span>
              <Checkbox
                color="default"
                checked={fleetViewEnabled}
                disabled={patchSettingsMutation.isLoading}
                onChange={handleSettingChange("fleetView")}
                className="companySlidePanelSettingsCheckbox"
              />
            </label>
            <label className="companySlidePanelSettingsRow">
              <span className="companySlidePanelSettingsItemLabel">Map</span>
              <Checkbox
                color="default"
                checked={mapEnabled}
                disabled={patchSettingsMutation.isLoading}
                onChange={handleSettingChange("map")}
                className="companySlidePanelSettingsCheckbox"
              />
            </label>
            <label className="companySlidePanelSettingsRow">
              <span className="companySlidePanelSettingsItemLabel">{translation.settingsApiKeysLabel || 'API Keys'}</span>
              <Checkbox
                color="default"
                checked={apiKeysEnabled}
                disabled={patchSettingsMutation.isLoading}
                onChange={handleSettingChange("apiKeys")}
                className="companySlidePanelSettingsCheckbox"
              />
            </label>
            <label className="companySlidePanelSettingsRow">
              <span className="companySlidePanelSettingsItemLabel">{translation.settingsWellboreDataLabel || 'Wellbore Data'}</span>
              <Checkbox
                color="default"
                checked={wellboreDataEnabled}
                disabled={patchSettingsMutation.isLoading}
                onChange={handleSettingChange("wellboreData")}
                className="companySlidePanelSettingsCheckbox"
              />
            </label>
          </div>
        </div>
      </div>
      <Popover
        open={Boolean(anchorCompanyMenuEl)}
        anchorEl={anchorCompanyMenuEl}
        onClose={handleCompanyMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <MenuItem
          onClick={() => {
            handleCompanyMenuClose();
            dispatch(setEditingCompany(company));
            setIsOpenSaveCompanyModal(true);
          }}
          id="companySlidePanelEditBtn"
        >
          <MyIcons
            icon="createIcon"
            className="asideNavPopoverIcon"
            style={{ width: "16px", height: "16px", marginRight: "8px" }}
          />
          {translation.sideMenuEditCompanyLabel}
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleCompanyMenuClose();
            inputCompanyLogoRef.current?.click();
          }}
          id="companySlidePanelChangeLogoBtn"
        >
          <MyIcons
            icon="photoCamera"
            className="asideNavPopoverIcon"
            style={{ width: "16px", height: "16px", marginRight: "8px" }}
          />
          {translation.sideMenuEditLogoLabel}
        </MenuItem>
      </Popover>
      <SaveCompanyModal
        open={isOpenSaveCompanyModal}
        handleClose={() => {
          setIsOpenSaveCompanyModal(false);
          refetchCompany();
        }}
      />
    </>
  );
};

export default CompanySlidePanelContent;
